// vg-binding: P7.D-132
// vg-binding: P7.D-134
// vg-binding: P7.D-142
// vg-binding: P7.D-147
//
// apps/vendor/src/components/VendorTopBar.tsx
//
// Phase 7 Wave 2 Task 06 — vendor SPA top bar (expanded shell).
//
// Phase 1 Wave 12 shipped a minimal variant — no theme toggle (vendor was
// light-only), no command-palette trigger. Phase 7 enables both:
//
//   - showThemeToggle={true} surfaces @printway/ui's ThemeToggle, which
//     persists `localStorage.theme` ∈ {light, dark, system} and toggles
//     the `.dark` class on <html>. Pairs with VendorAppShell where the
//     ThemeProvider was unpinned in P7-01 (forceTheme="light" removed).
//
//   - A keyboard-shortcut hint button (`Cmd+K`) sits in the topbar right
//     slot as a placeholder until Wave 5 Task 22 wires the real cmdk
//     dialog. Clicking it dispatches a `vendor:cmdk:open` CustomEvent on
//     window — Task 22 listens for that event and opens the command
//     palette dialog. The Cmd/Ctrl+K keyboard shortcut also dispatches
//     the same event, so we centralise the trigger here.
//
// Test IDs (PLAN/task-06 <test_ids>):
//   - topbar-notification-bell  (NotificationBell — Task 46 wires data)
//   - topbar-dark-mode-toggle   (data-testid forwarded onto ThemeToggle's
//                                wrapper; see VendorThemeToggleSlot below)
//   - topbar-cmdk-trigger       (CommandPaletteTrigger placeholder)
//   - topbar-user-menu          (AccountDropdown trigger)

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  AccountDropdown,
  HelpDropdown,
  SidebarToggle,
  ThemeToggle,
  TopBar,
  TopBarRightSlot,
  connectWs,
  useAuth,
  ensureNotificationPermission,
  showDesktopNotification,
  flashTitle,
  type WsClientHandle,
} from '@printway/ui';

import { useTranslation } from '../lib/i18n/index.js';

import { supportKeys } from '../features/support/api.js';
import { NotificationCenter } from './notifications/NotificationCenter';
import { AnnouncementCarousel } from '../features/announcements/AnnouncementCarousel';
// Task 79 (Amendment #1) — vendor-local LanguageSelector wired to the real
// i18next instance (lib/i18n). Replaces the placeholder one imported from
// `@printway/ui` (it defaulted to EN with no onChange wiring —).
import { LanguageSelector } from './LanguageSelector.js';

// Phase 7 sweep debug 2026-05-16 (C5/C9) — listen for `earning.created`
// + `order.status_changed` on the shared vendor WS channel and refetch
// the wallet/performance/orders queries so the UI updates within a
// second of the BE write instead of waiting the 30s refetch interval.
function useVendorRealtimeRefresh(client: WsClientHandle | null): void {
  const qc = useQueryClient();
  const { t } = useTranslation('support');

  // Ask for desktop-notification permission once (dbg-20260705).
  useEffect(() => {
    void ensureNotificationPermission();
  }, []);

  useEffect(() => {
    if (!client) return;
    const offs: Array<() => void> = [];
    offs.push(
      client.on('earning.created', () => {
        void qc.invalidateQueries({ queryKey: ['vendor', 'wallet', 'summary'] });
        void qc.invalidateQueries({ queryKey: ['vendor', 'wallet', 'transactions'] });
        void qc.invalidateQueries({ queryKey: ['vendor', 'performance'] });
      }),
    );
    offs.push(
      client.on('order.status_changed', () => {
        void qc.invalidateQueries({ queryKey: ['vendor', 'orders', 'list'] });
        void qc.invalidateQueries({ queryKey: ['vendor', 'print-queue'] });
      }),
    );
    offs.push(
      client.on('order.assigned', () => {
        void qc.invalidateQueries({ queryKey: ['vendor', 'orders', 'list'] });
        void qc.invalidateQueries({ queryKey: ['vendor', 'print-queue'] });
      }),
    );

    // Support ticket realtime (dbg-20260705). Vendor-raised events use the
    // `vendor.ticket.*` names; admin replies on assigned tickets ride the
    // shared `ticket.*` names (both delivered on ws:vendor:{id}).
    //
    // P1-27 fix — invalidate via the supportKeys factory. The old literal
    // ['vendor','support'] only matched the ASSIGNED surface; owned-ticket
    // queries live under ['vendor-tickets'] / ['vendor-ticket'] and never
    // refreshed on ticket.created / ticket.reply.
    const onTicketEvent = (): void => {
      void qc.invalidateQueries({ queryKey: supportKeys.ownedLists() });
      void qc.invalidateQueries({ queryKey: supportKeys.ownedDetails() });
      void qc.invalidateQueries({ queryKey: supportKeys.assignedRoot() });
      // Sound dropped (dbg-20260710) — browser notification + tab flash only.
      flashTitle(t('realtime.tabFlash'));
      showDesktopNotification(t('realtime.updateTitle'), {
        body: t('realtime.updateBody'),
        tag: 'vendor-ticket',
        onClick: () => {
          window.location.assign('/support');
        },
      });
    };
    for (const evt of [
      'vendor.ticket.created',
      'vendor.ticket.reply',
      'ticket.created',
      'ticket.reply',
    ]) {
      offs.push(client.on(evt, onTicketEvent));
    }

    return () => {
      for (const off of offs) off();
    };
  }, [client, qc, t]);
}

/**
 * Open a single shared WS client when the user is authenticated. Returns
 * the handle immediately (the underlying socket completes the ticket →
 * connect handshake async; NotificationCenter subscribes via `.on(...)`
 * + tolerates pre-open state). On logout, the handle is disconnected.
 */
function useVendorWsClient(): WsClientHandle | null {
  const [client, setClient] = useState<WsClientHandle | null>(null);
  const handleRef = useRef<WsClientHandle | null>(null);
  const user = useAuth((s) => s.user);
  const orgId = user?.vendorId ?? user?.id ?? '';

  useEffect(() => {
    if (!user || !orgId) {
      handleRef.current?.disconnect();
      handleRef.current = null;
      setClient(null);
      return;
    }
    if (handleRef.current) return;
    // Sandbox audit 2026-05-15 — relative ticketUrl resolved against the
    // vendor SPA host (vendor-v3.printway.work) which has no /api/* proxy
    // rule, so POST /api/v1/auth/ws-ticket hit the SPA fallback and
    // returned 405. Build absolute URL from VITE_API_BASE_URL (baked at
    // build time) when present so the request lands on api-v3.printway.work.
    // Falls back to relative path for Mode A localhost dev where the Vite
    // proxy still handles the rewrite + cookie scope.
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)
      ?? (import.meta.env.VITE_API_URL as string | undefined)
      ?? '';
    const ticketUrl = apiBase
      ? `${apiBase.replace(/\/$/, '')}/api/v1/auth/ws-ticket`
      : '/api/v1/auth/ws-ticket';
    const handle = connectWs({
      scope: 'vendor',
      orgId,
      ticketUrl,
    });
    handle.subscribe(`ws:user:${user.id}`);
    // Also subscribe to the vendor-scope channel so Phase 7 events
    // (order.assigned, earning.created, stock.low, etc.) reach this
    // browser tab. Subscribe-time RBAC guards on the BE side.
    handle.subscribe(`ws:vendor:${orgId}`);
    handleRef.current = handle;
    setClient(handle);
    return () => {
      handle.disconnect();
      if (handleRef.current === handle) {
        handleRef.current = null;
        setClient(null);
      }
    };
  }, [user, orgId]);

  return useMemo(() => client, [client]);
}

/** Custom event name the topbar dispatches; Task 22 listens for this. */
const CMDK_OPEN_EVENT = 'vendor:cmdk:open';

function CommandPaletteTrigger(): JSX.Element {
  const { t } = useTranslation('common');
  return (
    <button
      type="button"
      data-testid="topbar-cmdk-trigger"
      aria-label={t('topbar.openCommandPalette')}
      title={`${t('topbar.openCommandPalette')} (Cmd/Ctrl+K)`}
      onClick={() => {
        window.dispatchEvent(new CustomEvent(CMDK_OPEN_EVENT));
      }}
      className="hidden h-9 items-center gap-2 rounded-md border border-pw-border bg-pw-surface px-2.5 text-xs font-medium text-pw-text-muted transition-colors hover:bg-pw-bg hover:text-pw-text md:flex"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <span>{t('topbar.search')}</span>
      <kbd className="ml-2 inline-flex items-center gap-0.5 rounded border border-pw-border bg-pw-bg px-1 py-0.5 text-[10px] font-mono text-pw-text-muted">
        ⌘K
      </kbd>
    </button>
  );
}

/**
 * Mounts the local NotificationCenter dropdown carrying the
 * `topbar-notification-bell` testId hook. NotificationCenter renders the
 * bell trigger + unread badge + list dropdown + WS live updates
 * (G-VW-08). The shared @printway/ui NotificationBell was removed to
 * avoid duplicate bell icons.
 */
function NotificationBellSlot(props: {
  wsClient: WsClientHandle | null;
}): JSX.Element {
  return (
    <span data-testid="topbar-notification-bell" className="inline-flex">
      <NotificationCenter wsClient={props.wsClient} />
    </span>
  );
}

/**
 * Wraps the AccountDropdown so the user-menu test id sits on a stable
 * outer element rather than the dropdown's internal button.
 */
function AccountDropdownSlot(props: {
  name: string;
  avatarUrl: string | undefined;
  roleBadge: string;
  onLogout: () => void;
}): JSX.Element {
  return (
    <span data-testid="topbar-user-menu" className="inline-flex">
      <AccountDropdown {...props} />
    </span>
  );
}

/**
 * Keyboard shortcut: Cmd/Ctrl+K dispatches the same CustomEvent the
 * cmdk-trigger button fires. Task 22 wires the listener that opens the
 * palette dialog; until then it's a no-op observable in DevTools.
 */
function useCommandPaletteShortcut(): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const cmdOrCtrl = e.metaKey || e.ctrlKey;
      if (cmdOrCtrl && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent(CMDK_OPEN_EVENT));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export function VendorTopBar(): JSX.Element {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const wsClient = useVendorWsClient();
  useVendorRealtimeRefresh(wsClient);
  useCommandPaletteShortcut();
  const { t } = useTranslation('common');

  return (
    <TopBar
      logo={
        <SidebarToggle
          data-testid="topbar-sidebar-toggle"
          label={t('topbar.toggleSidebar')}
        />
      }
      // Render ThemeToggle manually inside the right slot so we can attach
      // the `topbar-dark-mode-toggle` test id wrapper. TopBar's bundled
      // toggle renders bare and doesn't accept extra props.
      showThemeToggle={false}
      userMenu={
        user ? (
          <TopBarRightSlot>
            <CommandPaletteTrigger />
            <NotificationBellSlot wsClient={wsClient} />
            <LanguageSelector />
            <HelpDropdown />
            {/* persists localStorage.theme + flips <html>.dark */}
            <span data-testid="topbar-dark-mode-toggle" className="inline-flex">
              <ThemeToggle />
            </span>
            <AccountDropdownSlot
              name={user.displayName || t('roleBadge.vendor')}
              avatarUrl={user.avatarUrl ?? undefined}
              roleBadge={t('roleBadge.vendor')}
              onLogout={() => {
                void logout();
              }}
            />
          </TopBarRightSlot>
        ) : null
      }
    >
      {/* Center slot — admin system announcements targeting portal=vendor,
          surface=topbar. Hidden on phones where the right slot fills the bar.
          (Vendor previously had NO announcement surface at all.) */}
      {user ? (
        <span className="hidden min-w-0 sm:inline-flex">
          <AnnouncementCarousel />
        </span>
      ) : null}
    </TopBar>
  );
}

export default VendorTopBar;
